/**
 * ProfileDetailsForm Component
 * Form for editing prospect profile details (name, position, physical stats)
 */

import { TextInput } from "../../../../../components/TextInput";
import { PillSelector } from "../../../../../components/PillSelector";

export interface ProfileFormData {
  firstName: string;
  lastName: string;
  position: string;
  gradYear: string;
  heightFeet: string;
  heightInches: string;
  weight: string;
  bats: string | null;
  throws: string | null;
}

export interface FormErrors {
  firstName?: string;
  lastName?: string;
  [key: string]: string | undefined;
}

interface ProfileDetailsFormProps {
  formData: ProfileFormData;
  formErrors: FormErrors;
  onFormChange: (data: Partial<ProfileFormData>) => void;
  onClearError: (field: keyof FormErrors) => void;
  disabled: boolean;
}

export function ProfileDetailsForm({
  formData,
  formErrors,
  onFormChange,
  onClearError,
  disabled,
}: ProfileDetailsFormProps) {
  return (
    <div className="prospect-viewer-panel__form-section">
      <TextInput
        label="First Name"
        value={formData.firstName}
        onChange={(e) => {
          onFormChange({ firstName: e.target.value });
          onClearError("firstName");
        }}
        error={formErrors.firstName}
        disabled={disabled}
      />

      <TextInput
        label="Last Name"
        value={formData.lastName}
        onChange={(e) => {
          onFormChange({ lastName: e.target.value });
          onClearError("lastName");
        }}
        error={formErrors.lastName}
        disabled={disabled}
      />

      <TextInput
        label="Position"
        value={formData.position}
        onChange={(e) => onFormChange({ position: e.target.value })}
        placeholder="Catcher"
        disabled={disabled}
      />

      <TextInput
        label="Grad Year"
        type="number"
        value={formData.gradYear}
        onChange={(e) => onFormChange({ gradYear: e.target.value })}
        placeholder="2025"
        disabled={disabled}
      />

      <div className="prospect-viewer-panel__form-row">
        <TextInput
          label="Height (ft)"
          type="number"
          value={formData.heightFeet}
          onChange={(e) => onFormChange({ heightFeet: e.target.value })}
          placeholder="6"
          disabled={disabled}
        />
        <TextInput
          label="Height (in)"
          type="number"
          value={formData.heightInches}
          onChange={(e) => onFormChange({ heightInches: e.target.value })}
          placeholder="1"
          disabled={disabled}
        />
      </div>

      <TextInput
        label="Weight"
        type="number"
        value={formData.weight}
        onChange={(e) => onFormChange({ weight: e.target.value })}
        placeholder="158"
        disabled={disabled}
      />

      <PillSelector
        label="Bats"
        options={[
          { label: "R", value: "R" },
          { label: "L", value: "L" },
          { label: "S", value: "S" },
        ]}
        value={formData.bats}
        onSelect={(value) => onFormChange({ bats: value })}
      />

      <PillSelector
        label="Throws"
        options={[
          { label: "R", value: "R" },
          { label: "L", value: "L" },
          { label: "S", value: "S" },
        ]}
        value={formData.throws}
        onSelect={(value) => onFormChange({ throws: value })}
      />
    </div>
  );
}
